import { FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from 'react-daisyui';
import { Link } from 'react-router-dom';
import Routes from '@/router/Routes.ts';
import { useAuthContext } from '@/context/AuthContext.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import { sendResetPasswordLinkSchema, SendResetPasswordLinkRequest } from '$/requests/SendResetPasswordLink.ts';

const ForgotPasswordPage: FC = () => {
  const { t } = useTranslation();
  const { control, handleSubmit } = useForm<SendResetPasswordLinkRequest>({
    resolver: zodResolver(sendResetPasswordLinkSchema),
    defaultValues: {
      email: '',
    },
  });
  const {
    sendResetPasswordLinkQuery: {
      mutateAsync: resetPassword,
      isPending: isLoadingResetPassword,
      error: errorResetPassword,
      isError: isErrorResetPassword,
      isSuccess: isSuccessResetPassword,
    },
  } = useAuthContext();

  const isLoading = isLoadingResetPassword;

  const handleSendResetPasswordLink = (data: SendResetPasswordLinkRequest) => {
    void (async () => {
      try {
        if (isLoading) return;

        await resetPassword({
          email: data.email,
        });
      } catch (error) {
        console.error(error);
      }
    })();
  };

  return (
    <BrandCardSideContent>
      <form className="w-[428px] max-w-full flex flex-col gap-4" onSubmit={handleSubmit(handleSendResetPasswordLink)}>
        <div className="flex flex-col gap-2 mb-2">
          <h1 className="font-feature font-bold text-2xl">{t('forgotPasswordPage.header')}</h1>
          <p className="text-sm text-text-secondary">{t('forgotPasswordPage.subheader')}</p>
        </div>
        <div className="flex flex-col gap-4">
          {isSuccessResetPassword && <Alert status="success">{t('forgotPasswordPage.successInfo')}</Alert>}
          {isErrorResetPassword && <Alert status="error">{errorResetPassword?.message}</Alert>}
          <Controller
            control={control}
            name="email"
            render={({ field: { onChange, value }, formState: { errors } }) => (
              <TextInput
                aria-label={t('label.email')}
                error={errors.email?.message}
                value={value}
                onChange={onChange}
                disabled={isSuccessResetPassword}
                placeholder={t('placeholder.email')}
              />
            )}
          />
          <LoadingButton
            className="mt-2"
            loading={isLoading}
            disabled={isSuccessResetPassword}
            type="submit"
            color="primary"
          >
            {t('button.send')}
          </LoadingButton>
          <div className="flex justify-start">
            <Link to={Routes.LOGIN}>
              <Button color="ghost" type="button">
                {t('button.goBack')}
              </Button>
            </Link>
          </div>
        </div>
      </form>
    </BrandCardSideContent>
  );
};

export default ForgotPasswordPage;
